import React from "react";
import {
  ArrowRightIcon,
  BriefcaseIcon,
  CircleCheckIcon,
  EyeIcon,
  MessageIcon,
  NetworkIcon,
  ScanFaceIcon,
  SparklesIcon,
} from "./landing/Icons";
import { LeadFormProvider } from "./landing/lead-form/LeadFormProvider";
import SupportTierCards from "./landing/programmes/SupportTierCards";
import {
  Breadcrumb,
  CONTAINER,
  GRID_BACKDROP,
  PrimaryLink,
  Section,
  SectionHeading,
  SectionLabel,
  SecondaryLink,
} from "./landing/ui";

const STALL_REASONS = [
  {
    Icon: MessageIcon,
    title: "Technically strong, but hesitant to speak up or take up space",
    body: "Capability is there, yet ideas stay unspoken and leadership moments are allowed to pass.",
  },
  {
    Icon: EyeIcon,
    title: "Doing leadership-level work that stays invisible",
    body: "Contribution grows beyond the role, but ownership and impact are not communicated with enough intention.",
  },
  {
    Icon: BriefcaseIcon,
    title: "Development that stops at a workshop and never reaches day-to-day work",
    body: "The learning feels useful in the room, but there is no supported practice when real leadership situations arrive.",
  },
] as const;

const TRACKS = [
  {
    Icon: SparklesIcon,
    name: "Emerging track",
    audience: "For early-to-mid-career women who are technically credible but may hesitate to take up space.",
    focus: [
      "Confidence to speak up",
      "Self-trust and judgement",
      "Leadership identity",
      "Intentional visibility",
      "Ownership beyond role scope",
    ],
  },
  {
    Icon: NetworkIcon,
    name: "Advanced track",
    audience: "For senior individual contributors and people managers moving toward broader leadership influence.",
    focus: [
      "Leadership judgement",
      "Stakeholder influence",
      "Leadership stance",
      "Systems thinking",
      "Broader leadership scope",
    ],
  },
] as const;

const PHASES = [
  {
    title: "Deliberate pause",
    when: "Onboarding, matching and sessions 1–2",
    body: "Create space for reflection, clarity and self-awareness before deciding what needs to shift.",
  },
  {
    title: "Stretch",
    when: "Mid-point group learning circle and session 3",
    body: "Test new behaviours through guided mentoring, practical challenges and honest reflection.",
  },
  {
    title: "Emergence",
    when: "Session 4, graduation and final impact report",
    body: "Build those behaviours into daily work and capture evidence of how leadership practice changed.",
  },
] as const;

const LEVELS = [
  { title: "Mindset", body: "Confidence, clarity and readiness" },
  { title: "Behaviour", body: "Approaching leadership situations differently" },
  { title: "Application", body: "Putting behaviours into practice at work" },
  { title: "Outcomes", body: "Evidence that an action produced a result" },
] as const;

// From the reference site's client example; confirm the figures before this goes live.
const CLIENT_EXAMPLE = {
  setup: "This client chose 6 months, two tracks and FutureLab industry mentors.",
  context: "27 women across two tracks, mentored by FutureLab industry mentors over 6 months in 2026.",
  results: [
    { value: "89%", label: "stayed active through the programme" },
    { value: "92%", label: "completed all four mentoring sessions" },
    { value: "100%", label: "put at least one new leadership behaviour into practice at work" },
    { value: "90%", label: "gave a concrete example of applying it at work" },
    { value: "85%", label: "said mentoring contributed significantly to the change" },
  ],
  emerging:
    "78% reported more confidence speaking up; 80% of mentors independently observed the same; 90% of mentors saw at least one new behaviour put into practice.",
  advanced:
    "55% reported more confidence taking on broader leadership responsibility; 55% stepped into opportunities with wider leadership impact; 57% of mentors saw their mentee take on broader responsibility or scope.",
  source: "Post-programme evaluation, 2026: 20 of 24 active mentees and 24 mentors responded.",
  mentorExperience: "In the senior track, 100% of mentors felt well supported by FutureLab, and 93% would mentor again.",
} as const;

const STORIES = [
  {
    title: "Speaking up with clients",
    body: "Worried about saying the wrong thing, she began preparing and pre-aligning before client meetings, spoke up with confidence and was recognised by the client.",
  },
  {
    title: "A courageous conversation",
    body: "With her mentor’s help, she prepared for a difficult career conversation with her manager and got the outcome she wanted to negotiate.",
  },
  {
    title: "From individual work to system change",
    body: "She took ownership of a new cost-control procedure, won her superior’s support and is taking it toward rollout across departments.",
  },
  {
    title: "Stretching beyond her expertise",
    body: "She used to see herself in a supporting role. When given work outside her area, she took it on and delivered it.",
  },
] as const;

const REFLECTIONS = [
  {
    quote:
      "The most valuable part was gaining confidence to speak up. My mentor created a safe space to test ideas, refine how I communicate, and practice contributing perspectives beyond my immediate scope.",
    who: "Programme participant, emerging track",
  },
  {
    quote:
      "Mentorship is most impactful when it goes beyond advice-giving. It should help women build confidence, navigate organisational dynamics, expand their networks, and identify opportunities for growth.",
    who: "Programme mentor",
  },
  { quote: "Women underestimated their power as leaders.", who: "Programme mentor" },
] as const;

type Props = {
  programmePlanUrl: string;
  programmesUrl: string;
  businessCaseUrl: string;
};

export default function WomenLeadershipPage({ programmePlanUrl, programmesUrl, businessCaseUrl }: Props) {
  return (
    <LeadFormProvider>
      <section className="tw-relative tw-bg-[#f4f8fc] tw-pb-24 tw-pt-20 sm:tw-pb-16 sm:tw-pt-10">
        <div aria-hidden className="tw-pointer-events-none tw-absolute tw-inset-0 tw-opacity-40" style={GRID_BACKDROP} />

        <div
          className={`${CONTAINER} tw-relative tw-grid tw-grid-cols-[1.08fr_.72fr] tw-items-end tw-gap-10 sm:tw-grid-cols-1`}
        >
          <div className="tw-min-w-0">
            <Breadcrumb current="Women in leadership" parents={[{ label: "Programmes", href: programmesUrl }]} />
            {/* The approved H1, mirrored in static_pages/women_leadership.html.erb; keep both in step. */}
            <h1 className="tw-mt-6 tw-mb-0 tw-max-w-4xl tw-text-[clamp(2.75rem,5.1vw,4.5rem)] sm:tw-text-[clamp(2.25rem,8vw,2.75rem)] tw-font-black tw-leading-[1.02] tw-tracking-[-.05em] tw-text-[#10233f]">
              A women in leadership mentoring programme that turns reflection into action
            </h1>
            <p className="tw-mt-6 tw-mb-0 tw-max-w-3xl tw-text-xl tw-leading-8 tw-text-[#536a82] sm:tw-text-lg">
              FutureLab&rsquo;s women leadership mentoring programme helps HR, DEI and L&amp;D teams in Malaysia
              and Southeast Asia develop women for broader leadership. You choose the tracks, mentors, timeline
              and format; our platform and programme support help participants practise new behaviours and give
              leadership evidence of workplace change.
            </p>
            <p className="tw-mt-4 tw-mb-0 tw-text-sm tw-font-bold tw-text-[#5b7188]">
              Last updated <time dateTime="2026-10-01">1 October 2026</time>
            </p>

            <div className="tw-mt-8 tw-flex tw-flex-row tw-flex-wrap tw-gap-3 sm:tw-flex-col">
              <PrimaryLink href={programmePlanUrl} data-lead-form="programme_plan">
                Get a women in leadership programme plan
                <ArrowRightIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
              </PrimaryLink>
              <SecondaryLink href={businessCaseUrl}>Build your business case</SecondaryLink>
            </div>
          </div>

          <aside
            aria-label="Typical women in leadership programme"
            className="tw-min-w-0 tw-rounded-[28px] tw-border tw-border-[#c5dcea] tw-bg-white tw-p-8 tw-shadow-[0_24px_60px_rgba(23,72,111,.12)] xs:tw-p-6"
          >
            <SectionLabel>Typical example</SectionLabel>
            <p className="tw-mt-4 tw-mb-0 tw-text-4xl tw-font-black tw-tracking-[-.045em] tw-text-[#173754]">6 months</p>
            <p className="tw-mt-6 tw-mb-0 tw-border-0 tw-border-t tw-border-solid tw-border-[#d8e6f0] tw-pt-6 tw-font-bold tw-leading-7 tw-text-[#304d68]">
              Four one-to-one mentoring sessions, with a group check-in at the halfway point.
            </p>
            <p className="tw-mt-4 tw-mb-0 tw-leading-7 tw-text-[#60768c]">
              Length, format and mentors can all be adjusted.
            </p>
          </aside>
        </div>
      </section>

      <Section>
        <div className="tw-max-w-3xl">
          <SectionLabel>Where momentum gets lost</SectionLabel>
          <SectionHeading>Why capable women stall before leadership</SectionHeading>
        </div>

        <div className="tw-mt-12 tw-grid tw-grid-cols-3 tw-gap-5 sm:tw-mt-9 sm:tw-grid-cols-1">
          {STALL_REASONS.map(({ Icon, title, body }) => (
            <article key={title} className="tw-rounded-[26px] tw-border tw-border-[#d4e2ee] tw-bg-white tw-p-7 xs:tw-p-6">
              <span
                aria-hidden
                className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-[#eaf6fd] tw-text-[#087bd3]"
              >
                <Icon className="tw-h-6 tw-w-6" />
              </span>
              <h3 className="tw-mt-6 tw-mb-0 tw-text-2xl tw-font-black tw-leading-tight tw-tracking-[-.03em] tw-text-[#173754]">
                {title}
              </h3>
              <p className="tw-mt-4 tw-mb-0 tw-leading-7 tw-text-[#60768c]">{body}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section className="tw-border-y tw-border-[#d7e4ef] tw-bg-[#f4f8fc]">
        <div className="tw-max-w-3xl">
          <SectionLabel>Two tracks, adapted to where she is now</SectionLabel>
          <SectionHeading>What we help you build</SectionHeading>
          <p className="tw-mt-5 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#60768c]">
            Use one track, both tracks, or adapt the focus areas to your own leadership framework and talent
            priorities.
          </p>
        </div>

        <div className="tw-mt-12 tw-grid tw-grid-cols-2 tw-gap-5 sm:tw-mt-9 sm:tw-grid-cols-1">
          {TRACKS.map(({ Icon, name, audience, focus }) => (
            <article
              key={name}
              className="tw-rounded-[28px] tw-border tw-border-[#c8deec] tw-bg-white tw-p-9 tw-shadow-[0_14px_40px_rgba(32,75,110,.06)] xs:tw-p-6"
            >
              <Icon aria-hidden className="tw-h-7 tw-w-7 tw-text-[#087bd3]" />
              <h3 className="tw-mt-5 tw-mb-0 tw-text-3xl tw-font-black tw-tracking-[-.035em] tw-text-[#173754]">{name}</h3>
              <p className="tw-mt-3 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#60768c]">{audience}</p>

              <h4 className="tw-mt-8 tw-mb-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.15em] tw-text-[#0068b8]">
                Possible focus areas
              </h4>
              <ul className="tw-mt-4 tw-mb-0 tw-grid tw-list-none tw-grid-cols-2 tw-gap-3 tw-p-0 xs:tw-grid-cols-1">
                {focus.map((area) => (
                  <li
                    key={area}
                    className="tw-flex tw-gap-2.5 tw-rounded-2xl tw-bg-[#f4f8fc] tw-px-4 tw-py-3 tw-text-sm tw-font-bold tw-leading-6 tw-text-[#304d68]"
                  >
                    <CircleCheckIcon aria-hidden className="tw-mt-1 tw-h-4 tw-w-4 tw-shrink-0 tw-text-[#0b8c72]" />
                    {area}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </Section>

      <Section>
        <div className="tw-grid tw-grid-cols-[.72fr_1.28fr] tw-items-end tw-gap-8 sm:tw-grid-cols-1 sm:tw-gap-5">
          <div>
            <SectionLabel>How it can run</SectionLabel>
            <SectionHeading size="compact">A typical six-month journey from deliberate pause to emergence</SectionHeading>
          </div>
          <p className="tw-m-0 tw-max-w-2xl tw-justify-self-end tw-text-lg tw-leading-8 tw-text-[#60768c] sm:tw-justify-self-start">
            This example uses four one-to-one mentoring sessions, a mid-point group check-in and mentors matched
            around each mentee&rsquo;s goals. We can adjust the journey to your timeline and preferred format.
          </p>
        </div>

        <ol
          aria-label="Six-month women in leadership programme journey"
          className="tw-mt-12 tw-mb-0 tw-grid tw-list-none tw-grid-cols-3 tw-gap-5 tw-p-0 sm:tw-mt-9 sm:tw-grid-cols-1"
        >
          {PHASES.map((phase, index) => (
            <li key={phase.title} className="tw-rounded-[24px] tw-border tw-border-[#d4e2ee] tw-bg-white tw-p-7 xs:tw-p-6">
              <span className="tw-text-xs tw-font-black tw-uppercase tw-tracking-[.14em] tw-text-[#0068b8]">
                Phase {index + 1}
              </span>
              <h3 className="tw-mt-4 tw-mb-0 tw-text-2xl tw-font-black tw-tracking-[-.03em] tw-text-[#173754]">{phase.title}</h3>
              <p className="tw-mt-2 tw-mb-0 tw-text-sm tw-font-bold tw-leading-6 tw-text-[#304d68]">{phase.when}</p>
              <p className="tw-mt-4 tw-mb-0 tw-leading-7 tw-text-[#60768c]">{phase.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      <section className="tw-bg-[#10233f] tw-py-24 tw-text-white sm:tw-py-20">
        <div className={CONTAINER}>
          <SectionLabel tone="mint">What we measure</SectionLabel>
          <SectionHeading>Evidence at four levels</SectionHeading>

          <ol className="tw-mt-10 tw-mb-0 tw-grid tw-list-none tw-grid-cols-4 tw-gap-4 tw-p-0 md:tw-grid-cols-2 xs:tw-grid-cols-1">
            {LEVELS.map((level, index) => (
              <li key={level.title} className="tw-rounded-2xl tw-border tw-border-white/15 tw-bg-white/[.06] tw-p-6">
                <span aria-hidden className="tw-text-sm tw-font-black tw-text-[#70d9ce]">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="tw-mt-3 tw-mb-0 tw-text-xl tw-font-black tw-text-white">{level.title}</h3>
                <p className="tw-mt-2 tw-mb-0 tw-leading-7 tw-text-[#b9cddd]">{level.body}</p>
              </li>
            ))}
          </ol>

          <p className="tw-mt-8 tw-mb-0 tw-text-lg tw-font-bold tw-leading-8 tw-text-white">
            Mentee and mentor evaluations are compared pair by pair, so results are not self-report alone.
          </p>
        </div>
      </section>

      <Section className="tw-bg-[#f4f8fc]">
        <div className="tw-max-w-4xl">
          <SectionLabel>Client example</SectionLabel>
          <SectionHeading>How one client ran it</SectionHeading>
          <p className="tw-mt-6 tw-mb-0 tw-text-lg tw-font-black tw-leading-8 tw-text-[#304d68]">{CLIENT_EXAMPLE.setup}</p>
          <p className="tw-mt-3 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#60768c]">{CLIENT_EXAMPLE.context}</p>
        </div>

        <div className="tw-mt-12 tw-grid tw-grid-cols-5 tw-gap-4 md:tw-grid-cols-3 sm:tw-mt-9 sm:tw-grid-cols-2 xs:tw-grid-cols-1">
          {CLIENT_EXAMPLE.results.map((result) => (
            <article key={result.label} className="tw-rounded-[24px] tw-border tw-border-[#c8deec] tw-bg-white tw-p-6">
              <p className="tw-m-0 tw-text-4xl tw-font-black tw-tabular-nums tw-tracking-[-.045em] tw-text-[#0068b8]">
                {result.value}
              </p>
              <p className="tw-mt-3 tw-mb-0 tw-font-bold tw-leading-7 tw-text-[#304d68]">{result.label}</p>
            </article>
          ))}
        </div>

        <div className="tw-mt-5 tw-grid tw-grid-cols-2 tw-gap-5 sm:tw-grid-cols-1">
          <div className="tw-rounded-[26px] tw-bg-[#087bd3] tw-p-9 tw-text-white xs:tw-p-7">
            <h3 className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.14em] tw-text-[#d9efff]">Emerging track</h3>
            <p className="tw-mt-4 tw-mb-0 tw-text-xl tw-font-black tw-leading-8">{CLIENT_EXAMPLE.emerging}</p>
          </div>
          <div className="tw-rounded-[26px] tw-bg-[#173754] tw-p-9 tw-text-white xs:tw-p-7">
            <h3 className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.14em] tw-text-[#70d9ce]">Advanced track</h3>
            <p className="tw-mt-4 tw-mb-0 tw-text-xl tw-font-black tw-leading-8">{CLIENT_EXAMPLE.advanced}</p>
          </div>
        </div>

        <p className="tw-mt-5 tw-mb-0 tw-text-xs tw-leading-5 tw-text-[#5b7188]">{CLIENT_EXAMPLE.source}</p>
      </Section>

      <Section>
        <SectionLabel>Stories of application</SectionLabel>
        <SectionHeading>What changed at work</SectionHeading>

        <div className="tw-mt-12 tw-grid tw-grid-cols-2 tw-gap-5 sm:tw-mt-9 sm:tw-grid-cols-1">
          {STORIES.map((story) => (
            <article key={story.title} className="tw-rounded-[24px] tw-border tw-border-[#d4e2ee] tw-bg-white tw-p-7 xs:tw-p-6">
              <ScanFaceIcon aria-hidden className="tw-h-6 tw-w-6 tw-text-[#087bd3]" />
              <h3 className="tw-mt-5 tw-mb-0 tw-text-xl tw-font-black tw-text-[#173754]">{story.title}</h3>
              <p className="tw-mt-3 tw-mb-0 tw-leading-7 tw-text-[#60768c]">{story.body}</p>
            </article>
          ))}
        </div>
      </Section>

      <Section className="tw-border-t tw-border-[#d7e4ef] tw-bg-[#f4f8fc]">
        <SectionLabel>Participant and mentor reflections</SectionLabel>
        <SectionHeading>What made the mentoring matter</SectionHeading>

        <div className="tw-mt-12 tw-grid tw-grid-cols-3 tw-gap-5 sm:tw-mt-9 sm:tw-grid-cols-1">
          {REFLECTIONS.map((reflection) => (
            <figure
              key={reflection.quote}
              className="tw-m-0 tw-flex tw-flex-col tw-justify-between tw-gap-8 tw-rounded-[24px] tw-border tw-border-[#c8deec] tw-bg-white tw-p-7 xs:tw-p-6"
            >
              <blockquote className="tw-m-0 tw-text-lg tw-font-black tw-leading-8 tw-tracking-[-.01em] tw-text-[#173754]">
                &ldquo;{reflection.quote}&rdquo;
              </blockquote>
              <figcaption className="tw-text-sm tw-font-bold tw-text-[#5b7188]">{reflection.who}</figcaption>
            </figure>
          ))}
        </div>
      </Section>

      <section className="tw-bg-[#087bd3] tw-py-14 tw-text-white sm:tw-py-10">
        <div className={`${CONTAINER} tw-grid tw-grid-cols-[.4fr_1.6fr] tw-items-center tw-gap-10 sm:tw-grid-cols-1 sm:tw-gap-4`}>
          <p className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.15em] tw-text-[#d9efff]">
            Mentor experience · Senior track
          </p>
          <p className="tw-m-0 tw-text-2xl tw-font-black tw-leading-snug tw-tracking-[-.02em] xs:tw-text-xl">
            {CLIENT_EXAMPLE.mentorExperience}
          </p>
        </div>
      </section>

      <Section>
        <div className="tw-max-w-3xl">
          <SectionLabel>Flexible by design</SectionLabel>
          <SectionHeading>Run it your way</SectionHeading>
          <p className="tw-mt-5 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#60768c]">
            You decide how the programme fits your people, systems and internal capacity. We provide the platform
            and the level of support you need.
          </p>
        </div>

        <div className="tw-mt-10">
          <SupportTierCards />
        </div>
      </Section>
    </LeadFormProvider>
  );
}
